import React, { useCallback, useEffect, useState } from "react";
import { useRouter } from "next/router";
import useApiAlert from "src/hooks/useApiAlert";
import useHttpErrorHandler from "src/hooks/useHttpErrorHandler";
import { ConfirmDialog } from "src/apps/commons/dataDisplays";
import { FormPaper } from "src/apps/commons/papers";
import * as Api from "../api";
import { IRemyndBrandContainer, IRemyndBrandSaveReqContainer } from "../types";
import Form from "./Form";
import { constants, paths } from "../utils";

interface Props {
  id?: RemyndBrandId;
}

interface StateTypes {
  fetching: boolean;
  data?: IRemyndBrandContainer;
}

interface ConfirmDialogStateTypes {
  open: boolean;
  onConfirm?: () => void;
}

const FormContainers: React.FC<Props> = props => {
  const { id } = props;

  const router = useRouter();
  const { setMessage } = useApiAlert();
  const { handleError } = useHttpErrorHandler();

  const [state, setState] = useState<StateTypes>({ fetching: true });
  const { fetching, data } = state;

  const [saving, setSaving] = useState(false);
  const [confirmDialogState, setConfirmDialogState] =
    useState<ConfirmDialogStateTypes>({ open: false });
  const handleCloseDialog = useCallback(() => {
    setConfirmDialogState({ open: false, onConfirm: undefined });
  }, []);
  const handleSave = useCallback(
    (req: IRemyndBrandSaveReqContainer) => {
      setConfirmDialogState({
        open: true,
        onConfirm: async () => {
          try {
            setSaving(true);
            if (id) {
              await Api.update(id, req);
            } else {
              await Api.save(req);
            }
            await router.push(paths.index);
            setMessage(
              "success",
              `${id ? "แก้ไข" : "สร้าง"} ${constants.label} "${
                req.brand.nameTh
              }" สำเร็จ`
            );
          } catch (e) {
            setSaving(false);
            handleError(e);
            handleCloseDialog();
          }
        }
      });
    },
    [id, setMessage, handleError, router, handleCloseDialog]
  );

  const fetchBrand = useCallback(
    async (brandId: RemyndBrandId) => {
      setState(d => ({ ...d, fetching: true }));
      try {
        const resp = await Api.get(brandId);
        setState({ fetching: false, data: resp.value });
      } catch (e) {
        handleError(e);
        setState({ fetching: false, data: undefined });
      }
    },
    [handleError]
  );

  useEffect(() => {
    if (id) {
      fetchBrand(id);
    } else {
      setState({ fetching: false, data: undefined });
    }
  }, [id, fetchBrand]);

  return (
    <FormPaper>
      <Form
        isLoading={fetching}
        isSaving={saving}
        data={data}
        onSubmit={handleSave}
      />

      <ConfirmDialog
        title={`ยืนยันการ${id ? "แก้ไข" : "สร้าง"}${constants.label}?`}
        open={confirmDialogState.open}
        onClose={handleCloseDialog}
        onConfirm={confirmDialogState.onConfirm}
        confirming={saving}
      />
    </FormPaper>
  );
};

export default FormContainers;
